import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { ClipboardList, CalendarClock, CheckCircle2, AlertCircle, ArrowRight, Clock } from 'lucide-react'
import { ProtectedRoute } from '../../components/ProtectedRoute'
import { useAuth } from '../../hooks/useAuth'
import { DEFAULT_COMPANY_ID } from '../../lib/config'
import { listMySessions, type AssessmentSessionDto } from '../../lib/assessmentSessions'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

export const Route = createFileRoute('/kinerja/my-assessments')({
  component: MyAssessmentsPage,
})

function MyAssessmentsPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <MyAssessmentsContent />
    </ProtectedRoute>
  )
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: AlertCircle, badgeVariant: 'warning' as const, actionLabel: 'Start' },
  in_progress: { label: 'In Progress', icon: Clock, badgeVariant: 'default' as const, actionLabel: 'Continue' },
  completed: { label: 'Completed', icon: CheckCircle2, badgeVariant: 'success' as const, actionLabel: 'View' },
  expired: { label: 'Expired', icon: AlertCircle, badgeVariant: 'secondary' as const, actionLabel: '' },
}

function MyAssessmentsContent() {
  const { user } = useAuth()
  const userId = user?.id ?? ''
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID

  const [sessions, setSessions] = useState<AssessmentSessionDto[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    listMySessions(userId, companyId).then((res) => {
      if (cancelled) return
      setLoading(false)
      if (res.success && res.data) setSessions(res.data.sessions)
    })
    return () => { cancelled = true }
  }, [userId, companyId])

  const actionable = sessions.filter((s) => s.status === 'pending' || s.status === 'in_progress')
  const finished = sessions.filter((s) => s.status === 'completed' || s.status === 'expired')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground tracking-tight">My Assessments</h1>
        <p className="text-sm text-muted-foreground mt-1">Your pending and completed performance reviews</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-4">
                  <div className="h-9 w-9 rounded-md bg-muted animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-48 bg-muted rounded animate-pulse" />
                    <div className="h-3 w-32 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-3">
            <div className="p-3 rounded-full bg-muted">
              <ClipboardList className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No assessments assigned to you yet.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {actionable.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Action Required
                </h2>
                <Badge variant="warning">{actionable.length}</Badge>
              </div>
              <div className="space-y-2">
                {actionable.map((s) => {
                  const cfg = STATUS_CONFIG[s.status]
                  const StatusIcon = cfg.icon
                  return (
                    <Card key={s.id} className="border-l-4 border-l-primary">
                      <CardContent className="py-4">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-md bg-brand-50 shrink-0">
                            <ClipboardList className="w-4 h-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm">{s.templateName ?? 'Assessment'}</p>
                            {s.dueDate && (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <CalendarClock className="w-3 h-3 text-muted-foreground" />
                                <p className="text-xs text-muted-foreground">
                                  Due {new Date(s.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant={cfg.badgeVariant} className="hidden sm:flex items-center gap-1">
                              <StatusIcon className="w-3 h-3" />
                              {cfg.label}
                            </Badge>
                            <Button size="sm" asChild>
                              <Link
                                to={'/kinerja/assessments/$sessionId' as never}
                                params={{ sessionId: s.id } as never}
                                className="flex items-center gap-1.5"
                              >
                                {cfg.actionLabel}
                                <ArrowRight className="w-3.5 h-3.5" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )}

          {actionable.length > 0 && finished.length > 0 && <Separator />}

          {finished.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">History</h2>
                <Badge variant="secondary">{finished.length}</Badge>
              </div>
              <div className="space-y-2">
                {finished.map((s) => {
                  const cfg = STATUS_CONFIG[s.status]
                  const StatusIcon = cfg.icon
                  return (
                    <Card key={s.id} className="opacity-80">
                      <CardContent className="py-4">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-md bg-muted shrink-0">
                            <StatusIcon className="w-4 h-4 text-brand-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm">{s.templateName ?? 'Assessment'}</p>
                            {s.completedAt && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Completed {new Date(s.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <Badge variant={cfg.badgeVariant} className="flex items-center gap-1 shrink-0">
                            <StatusIcon className="w-3 h-3" />
                            {cfg.label}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
